import { useEffect, useState } from "react";
import { FlaskConical } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Sticky orange strip rendered above all portal content whenever the seller
 * has Test Mode enabled. Pattern matches Shopify's "Test mode" banner.
 *
 * Seller portal: always reflects the seller's own tenant flag.
 * Buyer portal:  reflects the *active supplier* (so the buyer sees when
 *                they're shopping in a test storefront).
 *
 * Polls /api/tenant/test-mode on mount; refreshes whenever a `test-mode-changed`
 * window event is fired (the toggle card emits this so the banner reacts
 * instantly without a page reload).
 */
export default function TestModeBanner() {
  const { user } = useAuth();
  const role = user?.role;
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // Only sellers poll their own tenant. Buyer-side awareness is handled
    // per-invoice (TEST badge on the row) — not via a global banner — so we
    // don't accidentally light up the buyer portal when they're not in a
    // test supplier context.
    if (role !== "seller") return;
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/tenant/test-mode");
        if (alive) setEnabled(!!r.data?.enabled);
      } catch {
        if (alive) setEnabled(false);
      }
    };
    load();
    const onChange = () => load();
    window.addEventListener("test-mode-changed", onChange);
    return () => { alive = false; window.removeEventListener("test-mode-changed", onChange); };
  }, [role]);

  if (!enabled) return null;

  return (
    <div
      data-testid="test-mode-banner"
      className="sticky top-0 z-50 w-full bg-orange-500 text-white text-[13px] font-medium tracking-wide"
    >
      <div className="px-8 py-2 flex items-center justify-center gap-2">
        <FlaskConical className="w-4 h-4" />
        <span>
          TEST MODE — payments are simulated. No real money is moving.
        </span>
      </div>
    </div>
  );
}
